// Axial hex coordinates for a pointy-top layout.
// Reference: https://www.redblobgames.com/grids/hexagons/

export interface HexCoord {
  readonly q: number;
  readonly r: number;
}

export const Hex = (q: number, r: number): HexCoord => ({ q, r });

export const hexEquals = (a: HexCoord, b: HexCoord): boolean =>
  a.q === b.q && a.r === b.r;

export const hexKey = (h: HexCoord): string => `${h.q},${h.r}`;

// Neighbors in pointy-top axial: E, W, NE, NW, SE, SW
const AXIAL_DIRECTIONS: readonly HexCoord[] = [
  { q: 1, r: 0 },
  { q: -1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: 0, r: 1 },
  { q: -1, r: 1 },
];

export const hexNeighbors = (h: HexCoord): HexCoord[] =>
  AXIAL_DIRECTIONS.map((d) => ({ q: h.q + d.q, r: h.r + d.r }));

export const hexAdd = (a: HexCoord, b: HexCoord): HexCoord => ({
  q: a.q + b.q,
  r: a.r + b.r,
});

export const hexSub = (a: HexCoord, b: HexCoord): HexCoord => ({
  q: a.q - b.q,
  r: a.r - b.r,
});

// Cube distance: (|dq| + |dq+dr| + |dr|) / 2
export const hexDistance = (a: HexCoord, b: HexCoord): number => {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
};

// Euclidean distance in world space (pointy-top, size=1).
// Distinguishes hexes that are the same step-count apart but geometrically closer/farther.
export const hexEuclideanDist = (a: HexCoord, b: HexCoord): number => {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  const dx = Math.sqrt(3) * (dq + dr / 2);
  const dy = 1.5 * dr;
  return Math.sqrt(dx * dx + dy * dy);
};

// All hexes at exactly `radius` distance from center.
export const hexRing = (center: HexCoord, radius: number): HexCoord[] => {
  if (radius <= 0) return [center];
  const results: HexCoord[] = [];
  // Walk the ring: start at one corner, step along each edge.
  let h: HexCoord = {
    q: center.q + AXIAL_DIRECTIONS[4].q * radius,
    r: center.r + AXIAL_DIRECTIONS[4].r * radius,
  };
  for (let side = 0; side < 6; side++) {
    const dir = AXIAL_DIRECTIONS[side];
    for (let step = 0; step < radius; step++) {
      results.push(h);
      h = { q: h.q + dir.q, r: h.r + dir.r };
    }
  }
  return results;
};

// All hexes within `radius` distance of center (includes center).
export const hexesWithinRange = (center: HexCoord, radius: number): HexCoord[] => {
  const results: HexCoord[] = [];
  for (let dq = -radius; dq <= radius; dq++) {
    const rMin = Math.max(-radius, -dq - radius);
    const rMax = Math.min(radius, -dq + radius);
    for (let dr = rMin; dr <= rMax; dr++) {
      results.push({ q: center.q + dq, r: center.r + dr });
    }
  }
  return results;
};

// Cube coords for rounding (needed for line drawing).
interface Cube {
  x: number;
  y: number;
  z: number;
}

const axialToCube = (h: HexCoord): Cube => {
  const x = h.q;
  const z = h.r;
  const y = -x - z;
  return { x, y, z };
};

const cubeToAxial = (c: Cube): HexCoord => ({ q: c.x, r: c.z });

const cubeRound = (c: Cube): Cube => {
  let rx = Math.round(c.x);
  let ry = Math.round(c.y);
  let rz = Math.round(c.z);
  const xd = Math.abs(rx - c.x);
  const yd = Math.abs(ry - c.y);
  const zd = Math.abs(rz - c.z);
  if (xd > yd && xd > zd) rx = -ry - rz;
  else if (yd > zd) ry = -rx - rz;
  else rz = -rx - ry;
  return { x: rx, y: ry, z: rz };
};

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

const cubeLerp = (a: Cube, b: Cube, t: number): Cube => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  z: lerp(a.z, b.z, t),
});

// Line of hexes from a to b, inclusive.
export const hexLine = (a: HexCoord, b: HexCoord): HexCoord[] => {
  const dist = hexDistance(a, b);
  if (dist === 0) return [a];
  const ca = axialToCube(a);
  const cb = axialToCube(b);
  const result: HexCoord[] = [];
  for (let i = 0; i <= dist; i++) {
    result.push(cubeToAxial(cubeRound(cubeLerp(ca, cb, i / dist))));
  }
  return result;
};

// Extend a line from `from` through `through` to a total of `length` hexes
// past `from` (excluding `from`). Uses cube-space interpolation so diagonal
// casts produce a clean, duplicate-free line of hexes.
export const hexLineExtended = (
  from: HexCoord,
  through: HexCoord,
  length: number,
): HexCoord[] => {
  const dist = hexDistance(from, through);
  if (dist === 0) return [];
  // Project past `through` to a point exactly `length` hexes from `from`.
  const scale = length / dist;
  const projected: HexCoord = {
    q: from.q + (through.q - from.q) * scale,
    r: from.r + (through.r - from.r) * scale,
  };
  // hexLine handles fractional inputs via cube rounding; dedupe any repeats
  // that can occur at exact hex-edge midpoints.
  const line = hexLine(from, projected).slice(1);
  const seen = new Set<string>();
  const deduped: HexCoord[] = [];
  for (const h of line) {
    const k = `${h.q},${h.r}`;
    if (!seen.has(k)) {
      seen.add(k);
      deduped.push(h);
    }
  }
  return deduped;
};
